import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Recaptcha from "react-recaptcha";
import Button from "../../components/UI/Button";
import SummaryPopup from "./components/SummaryPopup";
import { CardsList } from "./components/CardsList";
import { OptionCard } from "./components/CardItem";
import { optionCardsData } from "../../data/optionCards";
import { optionDetails } from "../../data/optionDetails";
import { gmtOffset } from "utils";
import { encrypt } from "modules/ai-quote/utils/encrypt";

const replacePublicUrl = (files) => {
  return files.map((file) => {
    const name =
      file.title ||
      (file.url ? file.url.split("/").pop().replace("files-", "") : "unknown");
    const { title, url, ...rest } = file;
    return {
      ...rest,
      name: name,
      destination: url
        ? url.replace("public/", "https://calculator-backend.cdntest.info/")
        : undefined,
    };
  });
};

export const SummaryStep = ({ userData }) => {
  const [popup, setPopup] = useState(false);
  const [option, setOption] = useState({
    name: "",
    title: "",
    description: "",
    totalDays: 0,
    totalPrice: 0,
  });
  const [project, setProject] = useState("");
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const captchaLoadedRef = useRef(false);
  const recaptchaRef = useRef(null);
  const [captchaResponse, setCaptchaResponse] = useState("");

  const optionRef = useRef(option);
  const projectRef = useRef(project);

  useEffect(() => {
    optionRef.current = option;
  }, [option]);

  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  useEffect(() => {
    if (userData.supportOptions) {
      const supportOption = userData.supportOptions;
      const selectedOption = optionCardsData.find(
        (card) => card.name === supportOption
      );

      if (selectedOption) {
        setOption({
          name: selectedOption.name,
          title: selectedOption.title,
          description: selectedOption.description,
          totalDays: userData.AIResponse[0][supportOption]?.totalDays || 0,
          totalPrice: userData.AIResponse[0][supportOption]?.totalPrice || 0,
        });
      }
    }
  }, [userData]);

  const estimation = {
    elementor: userData.AIResponse[0].elementor,
    frontend: userData.AIResponse[0].frontend,
    wordpress: userData.AIResponse[0].wordpress,
  };

  const userID = userData.id;
  const data = JSON.stringify({ userID });
  const encryptedData = encrypt(data);

  const updatedUrls = replacePublicUrl(userData?.urls || []);
  const updatedOriginalFiles = replacePublicUrl(userData?.originalFiles || []);

  const sendEmailQuote = (captchaResponse) => {
    axios
      .post(
        `/order-now.html`,
        {
          attachments: {
            amazon: userData.backButton
              ? userData.originalFiles
              : updatedOriginalFiles,
            dropbox: [],
          },
          projectType: optionRef.current?.name || "",
          eta: {
            etaText: `${optionRef.current?.totalDays || 0} business days`,
            etaIsExists: false,
          },
          information: {
            name: userData.name,
            phone: userData.phone,
            email: userData.email,
            textarea: projectRef.current,
            isShowcaseProject: false,
            customerID: encryptedData,
            title: optionRef.current?.title || "",
            description: optionRef.current?.description || "",
            userSelected: userData.backButton ? userData.urls : updatedUrls,
          },
          modrewrite: userData.modrewrite,
          total: optionRef.current?.totalPrice || 0,
          gmtOffset: gmtOffset(),
          captcha: captchaResponse || "",
        },
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        setIsLoading(false);
        if (!response.data.error && response.data.redirect) {
          window.location.href = response.data.redirect;
        }
        if (response.data.error) {
          setErrorMessage(response.data.message);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage("An error occurred. Please try again.");
        console.error("Error sending request:", error);
      });
  };

  const handleVerifyCallback = (response) => {
    setTimeout(() => {
      sendEmailQuote(response);
    }, 1000); // 1 second timeout to send the request even if recaptcha didn't callback
  };

  const handleButtonClick = () => {
    setIsLoading(true);
    if (captchaLoadedRef.current && recaptchaRef.current) {
      recaptchaRef.current.execute();
    } else {
      console.error("Captcha not loaded or instance not found.");
    }
  };

  useEffect(() => {
    const loadCaptcha = () => {
      const captchaKey = window["recaptcha_key"];
      const captchaKeyExists = !!(captchaKey && captchaKey.length > 5);
      setCaptchaLoaded(captchaKeyExists);
      captchaLoadedRef.current = captchaKeyExists;
    };

    const timeout = setTimeout(loadCaptcha, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleProjectChange = (e) => {
    setProject(e.target.value);
    if (!hasStartedTyping) {
      setHasStartedTyping(true);
    }
  };

  return (
    <div className="app-ai-quote-summary">
      <div className="app-ai-quote-summary-frame">
        <CardsList>
          {optionCardsData.map((optionCard) => (
            <div className="cardsListColumn" key={optionCard.id}>
              <OptionCard
                data={optionCard}
                onClick={() =>
                  setOption({
                    name: optionCard.name,
                    title: optionCard.title,
                    description: optionCard.description,
                    totalDays: estimation[optionCard.name].totalDays,
                    totalPrice: estimation[optionCard.name].totalPrice,
                  })
                }
                selected={optionCard.name === option.name}
                price={
                  estimation[optionCard.name]
                    ? estimation[optionCard.name].totalPrice
                    : undefined
                }
                timeline={
                  estimation[optionCard.name]
                    ? estimation[optionCard.name].totalDays
                    : undefined
                }
              />
            </div>
          ))}
        </CardsList>
        <div className="app-ai-quote-summary-info-box">
          <h3>What&apos;s included in every quote:</h3>
          <hr />
          <ul className="app-ai-quote-summary-check-list">
            {optionDetails.map((item, index) => (
              <li key={index}>
                <span className="app-ai-quote-summary-check-list-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="app-ai-quote-summary-check-list-text-group">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="app-ai-quote-summary-info-box">
          <h3>Project brief:</h3>
          <hr />
          <textarea
            className="app-ai-quote-summary-textarea"
            placeholder="Specify your project details *"
            value={project}
            onChange={handleProjectChange}
            rows={3}
          />
          {hasStartedTyping && project.length < 4 && (
            <span className="app-ai-quote-website-error">
              Please provide a project brief of at least 4 characters.
            </span>
          )}
        </div>
      </div>

      <p>Please note, the AI-generated quote may not be final as parsing could be affected by design specifics. The final estimate will be confirmed by your project manager after reviewing the designs and specifications.</p>

      <Button
        onClick={handleButtonClick}
        disabled={option.length === 0 || isLoading || project.length < 4}
      >
        {isLoading ? "LOADING..." : "START A PROJECT"}
      </Button>

      {errorMessage && (
        <p className="app-ai-quote-error-message">{errorMessage}</p>
      )}

      {popup && <SummaryPopup trigger={popup} setTrigger={setPopup} />}

      {captchaLoaded && (
        <Recaptcha
          ref={recaptchaRef}
          badge="bottomleft"
          sitekey={window["recaptcha_key"]}
          size="invisible"
          verifyCallback={handleVerifyCallback}
        />
      )}
    </div>
  );
};
